import { model as i18n } from '!/i18n'
import { aye, nay } from '@/helpers'
import { api } from '@setplex/tria-api'
import { createEvent, sample, split } from 'effector'
import { not } from 'patronum'
import { model as cartModel } from '~/entities/cart'
import { model as paymentInfoModel } from '~/entities/payment-info'
import {
  getPriceSettingsExternalPaymentData,
  model as priceSettingsModel,
} from '~/entities/price-settings'
import { model as redirectUrlModel } from '~/entities/redirect-url'
import type { TvShowEpisodePayment } from '~/features/payment/interfaces'
import {
  filterEpisodePriceSettingsOnly,
  filterSeasonPriceSettingsOnly,
  getPaymentTvShowEpisodeMediaType,
  getPaymentTvShowEpisodePriceSettings,
  getPerItemPriceSettingsOnly,
  getPerSetPriceSettingsOnly,
  getTvShowEpisodePaymentTitle,
  getTvShowEpisodesLabels,
  isPaid,
  isPayForBundleItemEpisode,
  isPayForBundleItemSeason,
  isPayForBundleItemTvShow,
  isPayForItemEpisode,
  isPayForItemSeason,
  isPayForItemTvShow,
} from '../../lib'
import * as external from './external'
import { anyPaymentInitiated } from './model'

export const selectTvShowEpisode = createEvent<TvShowEpisodePayment>()

const selectForInternalPayment = createEvent<TvShowEpisodePayment>()

export const payForTvShowEpisode = createEvent<TvShowEpisodePayment>()

sample({
  clock: selectTvShowEpisode,
  fn: ({ id, seasonId, tvShowId, queryCategoryId, queryShowIndex }) => ({
    id,
    seasonId,
    tvShowId,
    queryCategoryId,
    queryShowIndex,
  }),
  target: redirectUrlModel.saveTvShowEpisodeRedirectUrl,
})

export const navigateToPlayer = createEvent<TvShowEpisodePayment>()
export const processTvShowEpisodePayment = createEvent<TvShowEpisodePayment>()
export const processByInternal = createEvent<TvShowEpisodePayment>()
export const processByExternal = createEvent<TvShowEpisodePayment>()

split({
  source: selectTvShowEpisode,
  match: {
    payment: (episode) => isPaid(episode),
    redirect: (episode) => !isPaid(episode),
  },
  cases: {
    payment: processTvShowEpisodePayment,
    redirect: navigateToPlayer,
  },
})

sample({
  clock: navigateToPlayer,
  target: redirectUrlModel.redirectToShowPlayer,
})

split({
  source: processTvShowEpisodePayment,
  match: {
    check: external.$externalPaymentEnabled,
    internal: not(external.$externalPaymentEnabled),
  },
  cases: {
    check: processByExternal,
    internal: processByInternal,
  },
})

sample({
  clock: processByExternal,
  fn: (episode) => {
    const { id, seasonId, tvShowId, externalId } = episode
    const priceSettings = getPaymentTvShowEpisodePriceSettings(episode)

    return {
      id,
      seasonId,
      tvShowId,
      mediaType: getPaymentTvShowEpisodeMediaType(episode),
      ...getPriceSettingsExternalPaymentData(priceSettings, externalId),
    }
  },
  target: external.selectForExternalPayment,
})

sample({
  clock: processByInternal,
  target: selectForInternalPayment,
})

// should be purchased
sample({
  clock: selectForInternalPayment,
  filter: ({ free, purchaseInfo }) => nay(free) && nay(purchaseInfo),
  target: payForTvShowEpisode,
})

// click on episode with episodePriceSettings with PER_ITEM payment method
// 1. Set price settings
sample({
  clock: payForTvShowEpisode,
  fn: (episode) => {
    // click on episode with episodePriceSettings with PER_ITEM payment method
    const filteredEpisodePriceSettings = filterEpisodePriceSettingsOnly(episode)
    const filteredSeasonPriceSettings = filterSeasonPriceSettingsOnly(episode)

    if (isPayForItemEpisode(episode) && filteredEpisodePriceSettings?.length) {
      return getPerItemPriceSettingsOnly(filteredEpisodePriceSettings)
    }

    // click on episode with episodePriceSettings with PER_SET payment method
    if (
      isPayForBundleItemEpisode(episode) &&
      filteredEpisodePriceSettings?.length
    ) {
      return getPerSetPriceSettingsOnly(filteredEpisodePriceSettings)
    }

    // click on episode with seasonPriceSettings with PER_ITEM payment method
    if (isPayForItemSeason(episode) && filteredSeasonPriceSettings?.length) {
      return getPerItemPriceSettingsOnly(filteredSeasonPriceSettings)
    }

    // click on episode with seasonPriceSettings with PER_SET payment method
    if (
      isPayForBundleItemSeason(episode) &&
      filteredSeasonPriceSettings?.length
    ) {
      return getPerSetPriceSettingsOnly(filteredSeasonPriceSettings)
    }

    // click on episode with tvShowPriceSettings with PER_ITEM payment method
    if (isPayForItemTvShow(episode)) {
      return getPerItemPriceSettingsOnly(episode.tvShowPriceSettings)
    }

    // click on episode with tvShowPriceSettings with PER_SET payment method
    if (isPayForBundleItemTvShow(episode)) {
      return getPerSetPriceSettingsOnly(episode.tvShowPriceSettings)
    }

    return null
  },
  target: priceSettingsModel.$priceSettings,
})

// click on episode with episodePriceSettings with PER_ITEM payment method
// 1. Set price settings
sample({
  clock: payForTvShowEpisode,
  filter: (episode) => {
    const filteredEpisodePriceSettings = filterEpisodePriceSettingsOnly(episode)

    return (
      isPayForItemEpisode(episode) &&
      Boolean(filteredEpisodePriceSettings?.length)
    )
  },
  fn: (episode) => {
    const filteredEpisodePriceSettings = filterEpisodePriceSettingsOnly(episode)

    return getPerItemPriceSettingsOnly(filteredEpisodePriceSettings)
  },
  target: priceSettingsModel.$priceSettings,
})

sample({
  clock: payForTvShowEpisode,
  target: anyPaymentInitiated,
})

// 2. Set ui payment info
sample({
  clock: payForTvShowEpisode,
  source: {
    show: api.tvshows.$currentTvShow,
    season: api.tvshows.$tvShowSeason,
    i18n: i18n.$t,
  },
  filter: (_, episode) => {
    const filteredEpisodePriceSettings = filterEpisodePriceSettingsOnly(episode)

    return (
      isPayForItemEpisode(episode) &&
      Boolean(filteredEpisodePriceSettings?.length)
    )
  },
  fn: ({ show, season, i18n: { t } }, episode) => {
    const { displayNumber, name, landscapeImageUrl, length } = episode
    const seasonNumber = season?.displayNumber
    const showName = show?.name ?? ''

    const title = getTvShowEpisodePaymentTitle({
      seasonNumber,
      episodeNumber: displayNumber,
      episodeName: name,
      t,
    })

    const filteredEpisodePriceSettings = filterEpisodePriceSettingsOnly(episode)

    return {
      name: showName,
      title,
      imageUrl: landscapeImageUrl,
      labels: [length],
      priceSettings: getPerItemPriceSettingsOnly(filteredEpisodePriceSettings),
    }
  },
  target: paymentInfoModel.$uiPaymentInfo,
})

// 3. Set payment info for cart
sample({
  clock: payForTvShowEpisode,
  filter: (episode) => {
    const filteredEpisodePriceSettings = filterEpisodePriceSettingsOnly(episode)

    return (
      isPayForItemEpisode(episode) &&
      Boolean(filteredEpisodePriceSettings?.length)
    )
  },
  fn: (episode) => ({
    episodeId: episode.id,
  }),
  target: cartModel.$tvShowEpisodeInfoForCart,
})

// click on episode with episodePriceSettings with PER_SET payment method
// 1. Set price settings
sample({
  clock: payForTvShowEpisode,
  filter: (episode) => {
    const filteredEpisodePriceSettings = filterEpisodePriceSettingsOnly(episode)

    return (
      isPayForBundleItemEpisode(episode) &&
      Boolean(filteredEpisodePriceSettings?.length)
    )
  },
  fn: (episode) => {
    const filteredEpisodePriceSettings = filterEpisodePriceSettingsOnly(episode)

    return getPerSetPriceSettingsOnly(filteredEpisodePriceSettings)
  },
  target: priceSettingsModel.$priceSettings,
})

// 2. Load bundle info (fill $priceSettings and $paymentInfoForCart in bundle file)
sample({
  clock: payForTvShowEpisode,
  filter: (episode) => {
    const filteredEpisodePriceSettings = filterEpisodePriceSettingsOnly(episode)

    return (
      isPayForBundleItemEpisode(episode) &&
      Boolean(filteredEpisodePriceSettings?.length)
    )
  },
  fn: (episode) => {
    const filteredEpisodePriceSettings = filterEpisodePriceSettingsOnly(episode)
    const priceSettings = getPerSetPriceSettingsOnly(
      filteredEpisodePriceSettings
    )

    return {
      bundleId: priceSettings?.[0].bundle?.id ?? 0,
    }
  },
  target: api.showBundles.loadShowBundleInfoForContent,
})

// click on episode with seasonPriceSettings with PER_ITEM payment method
// 1. Set price settings
sample({
  clock: payForTvShowEpisode,
  filter: (episode) => {
    const filteredSeasonPriceSettings = filterSeasonPriceSettingsOnly(episode)
    const filteredEpisodePriceSettings = filterEpisodePriceSettingsOnly(episode)

    return (
      !filteredEpisodePriceSettings?.length &&
      isPayForItemSeason(episode) &&
      Boolean(filteredSeasonPriceSettings?.length)
    )
  },
  fn: (episode) => {
    const filteredSeasonPriceSettings = filterSeasonPriceSettingsOnly(episode)

    return getPerItemPriceSettingsOnly(filteredSeasonPriceSettings)
  },
  target: priceSettingsModel.$priceSettings,
})

// 2. Set ui payment info
sample({
  clock: payForTvShowEpisode,
  source: {
    show: api.tvshows.$currentTvShow,
    season: api.tvshows.$tvShowSeason,
    episodes: api.tvshows.$tvShowSeasonEpisodes,
    i18n: i18n.$t,
  },
  filter: (_, episode) => {
    const filteredSeasonPriceSettings = filterSeasonPriceSettingsOnly(episode)
    const filteredEpisodePriceSettings = filterEpisodePriceSettingsOnly(episode)
    return (
      !filteredEpisodePriceSettings?.length &&
      isPayForItemSeason(episode) &&
      Boolean(filteredSeasonPriceSettings?.length)
    )
  },
  fn: ({ show, season, episodes, i18n: { t } }, episode) => {
    const { name: seasonName, landscapeImageUrl: seasonImageUrl } = season ?? {}
    const year = season?.year
    const episodesQuantity = episodes.length

    const labels = getTvShowEpisodesLabels({ episodesQuantity, year, t })

    const filteredSeasonPriceSettings = filterSeasonPriceSettingsOnly(episode)

    return {
      name: show?.name ?? '',
      title: seasonName ?? '',
      imageUrl: seasonImageUrl,
      labels,
      priceSettings: getPerItemPriceSettingsOnly(filteredSeasonPriceSettings),
    }
  },
  target: paymentInfoModel.$uiPaymentInfo,
})

// 3. Set payment info for cart
sample({
  clock: payForTvShowEpisode,
  source: {
    season: api.tvshows.$tvShowSeason,
  },
  filter: ({ season }, episode) => {
    const filteredSeasonPriceSettings = filterSeasonPriceSettingsOnly(episode)
    const filteredEpisodePriceSettings = filterEpisodePriceSettingsOnly(episode)

    return (
      !filteredEpisodePriceSettings?.length &&
      isPayForItemSeason(episode) &&
      Boolean(filteredSeasonPriceSettings?.length) &&
      aye(season)
    )
  },
  fn: ({ season }) => ({
    seasonId: season!.id,
  }),
  target: cartModel.$tvShowSeasonInfoForCart,
})

// click on episode with seasonPriceSettings with PER_SET payment method
// 1. Set price settings
sample({
  clock: payForTvShowEpisode,
  filter: (episode) => {
    const filteredSeasonPriceSettings = filterSeasonPriceSettingsOnly(episode)
    const filteredEpisodePriceSettings = filterEpisodePriceSettingsOnly(episode)

    return (
      !filteredEpisodePriceSettings?.length &&
      isPayForBundleItemSeason(episode) &&
      Boolean(filteredSeasonPriceSettings?.length)
    )
  },
  fn: (episode) => {
    const filteredSeasonPriceSettings = filterSeasonPriceSettingsOnly(episode)

    return getPerSetPriceSettingsOnly(filteredSeasonPriceSettings)
  },
  target: priceSettingsModel.$priceSettings,
})

// 2. Load bundle info (fill $priceSettings and $paymentInfoForCart in bundle file)
sample({
  clock: payForTvShowEpisode,
  source: {
    season: api.tvshows.$tvShowSeason,
  },
  filter: (_, episode) => {
    const filteredSeasonPriceSettings = filterSeasonPriceSettingsOnly(episode)
    const filteredEpisodePriceSettings = filterEpisodePriceSettingsOnly(episode)

    return (
      !filteredEpisodePriceSettings?.length &&
      isPayForBundleItemSeason(episode) &&
      Boolean(filteredSeasonPriceSettings?.length)
    )
  },
  fn: (_, episode) => {
    const filteredSeasonPriceSettings = filterSeasonPriceSettingsOnly(episode)

    const priceSettings = getPerSetPriceSettingsOnly(
      filteredSeasonPriceSettings
    )

    return {
      bundleId: priceSettings?.[0].bundle?.id ?? 0,
    }
  },
  target: api.showBundles.loadShowBundleInfoForContent,
})

// click on episode with tvShowPriceSettings with PER_ITEM payment method
// 1. Set price settings
sample({
  clock: payForTvShowEpisode,
  filter: (episode) => {
    const filteredSeasonPriceSettings = filterSeasonPriceSettingsOnly(episode)
    const filteredEpisodePriceSettings = filterEpisodePriceSettingsOnly(episode)

    return (
      !filteredEpisodePriceSettings?.length &&
      !filteredSeasonPriceSettings?.length &&
      isPayForItemTvShow(episode)
    )
  },
  fn: ({ tvShowPriceSettings }) =>
    getPerItemPriceSettingsOnly(tvShowPriceSettings),
  target: priceSettingsModel.$priceSettings,
})

// 2. Set ui payment info
sample({
  clock: payForTvShowEpisode,
  source: {
    show: api.tvshows.$currentTvShow,
    season: api.tvshows.$tvShowSeason,
    episodes: api.tvshows.$tvShowSeasonEpisodes,
    i18n: i18n.$t,
  },
  filter: (_, episode) => {
    const filteredSeasonPriceSettings = filterSeasonPriceSettingsOnly(episode)
    const filteredEpisodePriceSettings = filterEpisodePriceSettingsOnly(episode)

    return (
      !filteredEpisodePriceSettings?.length &&
      !filteredSeasonPriceSettings?.length &&
      isPayForItemTvShow(episode)
    )
  },
  fn: ({ show, i18n: { t } }, episode) => {
    const {
      ageRatings = '',
      year,
      name = '',
      landscapeImageUrl: showImageUrl,
    } = show ?? {}

    const labels = [ageRatings, String(year)].filter(Boolean)

    return {
      name,
      title: t('series'),
      imageUrl: showImageUrl,
      labels,
      priceSettings: getPerItemPriceSettingsOnly(episode.tvShowPriceSettings),
    }
  },
  target: paymentInfoModel.$uiPaymentInfo,
})

// 3. Set payment info for cart
sample({
  clock: payForTvShowEpisode,
  source: api.tvshows.$currentTvShow,
  filter: (tvShow, episode) => {
    const filteredSeasonPriceSettings = filterSeasonPriceSettingsOnly(episode)
    const filteredEpisodePriceSettings = filterEpisodePriceSettingsOnly(episode)

    return (
      !filteredEpisodePriceSettings?.length &&
      !filteredSeasonPriceSettings?.length &&
      isPayForItemTvShow(episode) &&
      aye(tvShow)
    )
  },
  fn: (tvShow) => ({
    tvShowId: tvShow!.id,
  }),
  target: cartModel.$tvShowInfoForCart,
})

// click on episode with tvShowPriceSettings with PER_SET payment method
// 1. Set price settings
sample({
  clock: payForTvShowEpisode,
  filter: (episode) => {
    const filteredSeasonPriceSettings = filterSeasonPriceSettingsOnly(episode)
    const filteredEpisodePriceSettings = filterEpisodePriceSettingsOnly(episode)

    return (
      !filteredEpisodePriceSettings?.length &&
      !filteredSeasonPriceSettings?.length &&
      isPayForBundleItemTvShow(episode)
    )
  },
  fn: ({ tvShowPriceSettings }) =>
    getPerSetPriceSettingsOnly(tvShowPriceSettings),
  target: priceSettingsModel.$priceSettings,
})

// 2. Load bundle info (fill $priceSettings and $paymentInfoForCart in bundle file)
sample({
  clock: payForTvShowEpisode,
  filter: (episode) => {
    const filteredSeasonPriceSettings = filterSeasonPriceSettingsOnly(episode)
    const filteredEpisodePriceSettings = filterEpisodePriceSettingsOnly(episode)

    return (
      !filteredEpisodePriceSettings?.length &&
      !filteredSeasonPriceSettings?.length &&
      isPayForBundleItemTvShow(episode)
    )
  },
  fn: ({ tvShowPriceSettings }) => {
    const priceSettings = getPerSetPriceSettingsOnly(tvShowPriceSettings)

    return {
      bundleId: priceSettings?.[0].bundle?.id ?? 0,
    }
  },
  target: api.showBundles.loadShowBundleInfoForContent,
})
